import type { ComponentType, ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';

/**
 * Navegação para as telas da base de conhecimento.
 *
 * Mesmo motivo de `features/kedb/navegacao.tsx`: o `to` do TanStack é tipado
 * contra a árvore registrada em `app/router.tsx`, e este módulo é compilado
 * antes de a rota existir lá.
 *
 * ⚠ Os caminhos são **literais**, e não `: string`. Anotá-los como `string`
 * compila antes da integração, mas apaga o literal de que o roteador precisa
 * para registrar a rota — e o sintoma aparece longe daqui, num `<Link>` de
 * outro arquivo que passa a não encontrar o destino. A saída correta é a
 * fachada `LinkDeRota` abaixo, que afrouxa o tipo em UM lugar.
 */

export const CAMINHO_ARTIGOS = '/conhecimento';
export const CAMINHO_ARTIGO = '/conhecimento/$artigoId';
export const CAMINHO_ARTIGO_NOVO = '/conhecimento/novo';
export const CAMINHO_ARTIGO_EDITAR = '/conhecimento/$artigoId/editar';

export const enderecoDoArtigo = (id: string): string => `${CAMINHO_ARTIGOS}/${id}`;

interface PropsDeLink {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
  className?: string;
  title?: string;
  children: ReactNode;
}

/**
 * A fachada que afrouxa o tipo, em um lugar só.
 *
 * Espalhar `as never` por cada `<Link>` esconderia o custo; concentrá-lo aqui
 * deixa uma linha para apagar no dia em que a árvore de rotas for tipada de
 * ponta a ponta.
 */
const LinkDeRota = Link as unknown as ComponentType<PropsDeLink>;

export function LinkDoArtigo({
  artigoId,
  className,
  title,
  children,
}: {
  artigoId: string;
  className?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <LinkDeRota to={CAMINHO_ARTIGO} params={{ artigoId }} className={className} title={title}>
      {children}
    </LinkDeRota>
  );
}

export function LinkDaLista({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <LinkDeRota to={CAMINHO_ARTIGOS} className={className}>
      {children}
    </LinkDeRota>
  );
}

/** Navegação imperativa, com o tipo afrouxado no mesmo lugar. */
export function useIrPara() {
  const navigate = useNavigate() as unknown as (opcoes: {
    to: string;
    params?: Record<string, string>;
    search?: Record<string, unknown>;
    replace?: boolean;
  }) => void;
  return navigate;
}

/**
 * O termo de busca vive na URL, e não no estado da tela: assim o resultado pode
 * ser mandado por mensagem, e recarregar não perde a pesquisa.
 */
export function useBuscaDeArtigos(): { termo: string; definir: (valor: string) => void } {
  const navegar = useIrPara();
  const busca = useRouterState({
    select: (estado) => estado.location.search as Record<string, unknown>,
  });
  const termo = typeof busca['q'] === 'string' ? (busca['q'] as string) : '';
  return {
    termo,
    definir: (valor: string) =>
      // `replace`: digitar não pode encher o histórico do navegador. Sem isto,
      // o botão "voltar" percorre letra por letra o que foi digitado.
      navegar({
        to: CAMINHO_ARTIGOS,
        search: valor.trim() === '' ? {} : { q: valor.trim() },
        replace: true,
      }),
  };
}
